// frontend/src/pages/PrivacyPolicyPage.jsx
// Privacy Policy - UniLedge LLC
// Last Updated: March 2026

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, ExternalLink } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const effectiveDate = "March 5, 2026";
  const termlyUrl = "https://app.termly.io/policy-viewer/policy.html?policyUUID=1abca451-363c-4da2-beda-209d5f4cf506";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => window.history.back()} className="flex items-center text-gray-600 hover:text-gray-900">
              <ArrowLeft size={20} className="mr-2" />
              <span>Back</span>
            </button>
            <div className="flex items-center">
              <Shield size={20} className="text-blue-600 mr-2" />
              <span className="font-semibold text-gray-900">UniLedge</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
          
          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-gray-600">UniLedge LLC</p>
            <p className="text-sm text-gray-500 mt-2">Effective Date: {effectiveDate}</p>
          </div>

          {/* Termly Link Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center">
                <Shield size={20} className="text-blue-600 mr-2" />
                <span className="text-blue-800 font-medium">Looking for the latest updates?</span>
              </div>
              <a 
                href={termlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Latest Privacy Policy
                <ExternalLink size={16} className="ml-2" />
              </a>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="bg-gray-50 rounded-lg p-4 mb-8">
            <h2 className="font-semibold text-gray-900 mb-3">Contents</h2>
            <ol className="text-sm text-blue-600 space-y-1">
              <li><a href="#introduction" className="hover:underline">1. Introduction</a></li>
              <li><a href="#information-collect" className="hover:underline">2. Information We Collect</a></li>
              <li><a href="#how-we-use" className="hover:underline">3. How We Use Your Information</a></li>
              <li><a href="#information-sharing" className="hover:underline">4. How We Share Your Information</a></li>
              <li><a href="#no-sale" className="hover:underline">5. We Do Not Sell Your Personal Information</a></li>
              <li><a href="#data-security" className="hover:underline">6. Data Security</a></li>
              <li><a href="#data-breach" className="hover:underline">7. Data Breach Notification</a></li>
              <li><a href="#cookies" className="hover:underline">8. Cookies and Tracking</a></li>
              <li><a href="#data-retention" className="hover:underline">9. Data Retention</a></li>
              <li><a href="#privacy-rights" className="hover:underline">10. Your Privacy Rights (CCPA)</a></li>
              <li><a href="#children" className="hover:underline">11. Children's Privacy</a></li>
              <li><a href="#international" className="hover:underline">12. International Data Transfers</a></li>
              <li><a href="#third-party" className="hover:underline">13. Third-Party Links</a></li>
              <li><a href="#changes" className="hover:underline">14. Changes to This Policy</a></li>
              <li><a href="#contact" className="hover:underline">15. Contact Us</a></li>
            </ol>
          </div>

          {/* Sections */}
          <div className="prose prose-gray max-w-none">
            
            {/* Section 1 */}
            <section id="introduction" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                UniLedge LLC ("UniLedge," "we," "us," or "our") operates the UniLedge platform, a group purchasing and economic coordination service that enables communities to pool their buying power for savings on everyday essentials. This Privacy Policy explains how we collect, use, disclose, retain, and safeguard your information when you use our website (uniledge.io), mobile applications, and related services (collectively, the "Platform").
              </p>
              <p className="text-gray-700 mb-4">
                UniLedge is committed to protecting your privacy and ensuring transparency about our data practices. We process personal information in accordance with the California Consumer Privacy Act (CCPA), the California Privacy Rights Act (CPRA), and other applicable privacy laws.
              </p>
              <p className="text-gray-700 font-semibold">
                BY USING THE PLATFORM, YOU CONSENT TO THE DATA PRACTICES DESCRIBED IN THIS PRIVACY POLICY. IF YOU DO NOT AGREE WITH THIS PRIVACY POLICY, PLEASE DO NOT ACCESS OR USE THE PLATFORM.
              </p>
            </section>

            {/* Section 2 */}
            <section id="information-collect" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.1 Account and Profile Information</h3>
              <p className="text-gray-700 mb-2">When you create an account or update your profile, we collect:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Full name, email address, and phone number</li>
                <li>Account credentials (password stored in hashed form)</li>
                <li>Profile photograph (optional)</li>
                <li>Mailing and delivery addresses</li>
                <li>Date of birth (for age verification)</li>
                <li>Referral information</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.2 Location and Geographic Information</h3>
              <p className="text-gray-700 mb-2">
                <strong>Location data is fundamental to UniLedge's group purchasing model.</strong> We use H3 hexagonal geographic indexing to automatically group nearby buyers together, enabling bulk savings without requiring manual coordination. We collect location through:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>GPS coordinates from your mobile device (with your permission)</li>
                <li>IP address geolocation for approximate location detection</li>
                <li>ZIP code or street address you provide</li>
                <li>Delivery addresses for order fulfillment</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>H3 Geographic Indexing:</strong> We convert your location into H3 hexagonal zones (approximately 5km/3 miles across) to group you with nearby buyers. This allows us to pool purchasing power without sharing your exact address with other users.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.3 Transaction and Financial Information</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Purchase history and order details</li>
                <li>Payment method information (processed and stored by Stripe; we do not store complete card numbers)</li>
                <li>Token balance, earnings, and redemption history</li>
                <li>Loyalty tier status and progression data</li>
                <li>Refund and dispute records</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.4 Job Marketplace and Skills Information</h3>
              <p className="text-gray-700 mb-2">If you participate in the job marketplace as a worker:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Skills and qualifications you report</li>
                <li>Professional certifications and licenses</li>
                <li>Job completion records and performance metrics</li>
                <li>Verification photographs with GPS metadata</li>
                <li>Ratings and reviews from job requesters</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.5 Trust Score and Platform Behavior Data</h3>
              <p className="text-gray-700 mb-2">UniLedge maintains a Trust Score system to ensure platform integrity. We collect:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Order completion and cancellation rates</li>
                <li>Payment reliability and dispute history</li>
                <li>Job performance metrics (for workers)</li>
                <li>Community feedback and ratings</li>
                <li>Policy compliance</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.6 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Device information (device type, operating system, unique identifiers)</li>
                <li>Browser type and version</li>
                <li>IP address</li>
                <li>Pages visited and navigation patterns</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.7 Communications and Support Data</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Customer support inquiries and responses</li>
                <li>Conversations with AVA, our AI assistant</li>
                <li>Feedback, surveys, and reviews you submit</li>
                <li>Email and SMS communications</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="how-we-use" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Platform Operations</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Provide, operate, and maintain the Platform</li>
                <li>Process transactions and send related information</li>
                <li>Facilitate group buying by connecting nearby purchasers</li>
                <li>Calculate and apply group pricing tiers</li>
                <li>Coordinate order fulfillment and delivery logistics</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Token Economy Management</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Manage your UniLedge token balance and transactions</li>
                <li>Calculate and award tokens for purchases, referrals, jobs, and achievements</li>
                <li>Process token redemptions and apply loyalty tier benefits</li>
                <li>Track token expiration and send reminder notifications</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Trust and Safety</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Calculate and maintain Trust Scores</li>
                <li>Detect and prevent fraudulent activity</li>
                <li>Enforce our Terms of Service</li>
                <li>Respond to legal requests and prevent harm</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section id="information-sharing" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. How We Share Your Information</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">With Vendors</h3>
              <p className="text-gray-700 mb-4">
                We share necessary information with vendors to fulfill your orders, including your name, delivery address, and order details. Vendors are contractually obligated to use this information solely for order fulfillment.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">With Group Members</h3>
              <p className="text-gray-700 mb-4">
                Limited information may be visible to facilitate group coordination. Other users in your buying group can see the number of participants and aggregate demand, but <strong>cannot see your name, exact address, or purchase details</strong>.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">With Service Providers</h3>
              <p className="text-gray-700 mb-2">We share information with third-party service providers including:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Stripe (payment processing)</li>
                <li>Firebase (authentication)</li>
                <li>MongoDB Atlas (database hosting)</li>
                <li>Amazon Web Services (cloud infrastructure)</li>
                <li>Cloudinary (image storage)</li>
                <li>SendGrid (email communications)</li>
                <li>Twilio (SMS notifications)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Aggregated and Anonymized Data</h3>
              <p className="text-gray-700 mb-4">
                We may share aggregated, anonymized data that cannot reasonably be used to identify you for research, marketing, or business purposes.
              </p>
            </section>

            {/* Section 5 */}
            <section id="no-sale" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. We Do Not Sell Your Personal Information</h2>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-gray-700 font-semibold">
                  UniLedge does not sell, rent, or trade your personal information to third parties for their marketing or commercial purposes. This is a core principle of our business.
                </p>
              </div>
              <p className="text-gray-700 mt-4">
                Under the California Consumer Privacy Act (CCPA), "sale" includes sharing personal information for monetary or other valuable consideration. We confirm that we have not sold personal information in the preceding twelve (12) months and do not intend to do so.
              </p>
            </section>

            {/* Section 6 */}
            <section id="data-security" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Data Security</h2>
              <p className="text-gray-700 mb-2">We implement comprehensive security measures:</p>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Technical Safeguards</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Encryption of data in transit using TLS 1.2 or higher</li>
                <li>Encryption of sensitive data at rest</li>
                <li>Secure password hashing using industry-standard algorithms</li>
                <li>Regular security assessments</li>
                <li>Web application firewall protection</li>
                <li>DDoS mitigation through Cloudflare</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Payment Security</h3>
              <p className="text-gray-700 mb-4">
                All payment processing is handled by Stripe, a PCI DSS Level 1 certified payment processor. We do not store complete credit card numbers on our systems.
              </p>

              <p className="text-gray-700 italic">
                <strong>Security Limitation:</strong> Despite our efforts, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information using commercially reasonable measures, we cannot guarantee absolute security.
              </p>
            </section>

            {/* Section 7 */}
            <section id="data-breach" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Data Breach Notification</h2>
              <p className="text-gray-700 mb-2">In the event of a data breach that compromises your personal information, we will:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Notify affected users within 72 hours of discovering the breach, where feasible</li>
                <li>Notify the California Attorney General if the breach affects more than 500 California residents</li>
                <li>Provide information about what data was affected and steps you can take to protect yourself</li>
                <li>Take immediate steps to contain and remediate the breach</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section id="cookies" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">8. Cookies and Tracking Technologies</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Essential Cookies</h3>
              <p className="text-gray-700 mb-4">
                Required for Platform functionality, including authentication, shopping cart, and security features. These cannot be disabled.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Analytics Cookies</h3>
              <p className="text-gray-700 mb-4">
                Help us understand how users interact with the Platform to improve our services. You may opt out through your browser settings.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Preference Cookies</h3>
              <p className="text-gray-700 mb-4">
                Remember your settings and preferences for a personalized experience.
              </p>

              <p className="text-gray-700">
                For more details, please see our <Link to="/cookies" className="text-blue-600 hover:underline">Cookie Policy</Link>.
              </p>
            </section>

            {/* Section 9 */}
            <section id="data-retention" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">9. Data Retention</h2>
              <p className="text-gray-700 mb-2">We retain your personal information for specific periods:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li><strong>Account information:</strong> Duration of account plus 3 years after closure</li>
                <li><strong>Transaction records:</strong> 7 years (for tax and legal compliance)</li>
                <li><strong>Token transaction history:</strong> 7 years</li>
                <li><strong>Job verification photos:</strong> 2 years after job completion</li>
                <li><strong>Support communications:</strong> 3 years</li>
                <li><strong>Analytics data:</strong> 2 years (anonymized thereafter)</li>
              </ul>
              <p className="text-gray-700">
                Upon account deletion request, we will delete or anonymize your personal information within 45 days, except for information we are required to retain for legal, tax, or regulatory purposes.
              </p>
            </section>

            {/* Section 10 */}
            <section id="privacy-rights" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">10. Your Privacy Rights (California Residents)</h2>
              <p className="text-gray-700 mb-4">
                Under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), California residents have the following rights:
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Right to Know</h3>
              <p className="text-gray-700 mb-4">
                You have the right to request information about the categories and specific pieces of personal information we have collected about you.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Right to Delete</h3>
              <p className="text-gray-700 mb-4">
                You have the right to request deletion of your personal information, subject to certain exceptions.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Right to Correct</h3>
              <p className="text-gray-700 mb-4">
                You have the right to request correction of inaccurate personal information.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Right to Non-Discrimination</h3>
              <p className="text-gray-700 mb-4">
                We will not discriminate against you for exercising any of your privacy rights.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Exercising Your Rights</h3>
              <p className="text-gray-700 mb-4">
                To exercise your privacy rights, contact us at <a href="mailto:privacy@uniledge.io" className="text-blue-600 hover:underline">privacy@uniledge.io</a>. We will verify your identity before processing your request and respond within 45 days.
              </p>
            </section>

            {/* Section 11 */}
            <section id="children" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">11. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                The Platform is intended for users who are at least 18 years of age, or users between 13 and 17 years of age with verifiable parental or guardian consent.
              </p>
              <p className="text-gray-700 mb-4">
                We do not knowingly collect personal information from children under 13. If we learn that we have collected personal information from a child under 13 without parental consent, we will take steps to delete that information promptly.
              </p>
              <p className="text-gray-700">
                If you believe a child under 13 has provided us with personal information, please contact us immediately at <a href="mailto:privacy@uniledge.io" className="text-blue-600 hover:underline">privacy@uniledge.io</a>.
              </p>
            </section>

            {/* Section 12 */}
            <section id="international" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">12. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                UniLedge is based in the United States and primarily serves users in California. Your information may be processed and stored in the United States and other countries where our service providers operate.
              </p>
              <p className="text-gray-700">
                If you are accessing the Platform from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States, where data protection laws may differ from those in your country of residence.
              </p>
            </section>

            {/* Section 13 */}
            <section id="third-party" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">13. Third-Party Links and Services</h2>
              <p className="text-gray-700">
                The Platform may contain links to third-party websites or integrate with third-party services. This Privacy Policy does not apply to those third parties. We encourage you to review their privacy policies before providing any personal information.
              </p>
            </section>

            {/* Section 14 */}
            <section id="changes" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">14. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-2">
                We may update this Privacy Policy from time to time. We will notify you of material changes by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Posting the updated Privacy Policy on this page</li>
                <li>Updating the "Effective Date" at the top</li>
                <li>Sending you an email notification (for significant changes)</li>
                <li>Displaying a prominent notice on the Platform</li>
              </ul>
              <p className="text-gray-700">
                Your continued use of the Platform after changes take effect constitutes acceptance of the revised Privacy Policy.
              </p>
            </section>

            {/* Section 15 */}
            <section id="contact" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">15. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-semibold text-gray-900 mb-2">UniLedge LLC</p>
                <div className="space-y-2 text-gray-700">
                  <p className="flex items-center">
                    <Mail size={16} className="mr-2 text-gray-500" />
                    Privacy Inquiries: <a href="mailto:privacy@uniledge.io" className="text-blue-600 hover:underline ml-1">privacy@uniledge.io</a>
                  </p>
                  <p className="flex items-center">
                    <Mail size={16} className="mr-2 text-gray-500" />
                    Legal Inquiries: <a href="mailto:legal@uniledge.io" className="text-blue-600 hover:underline ml-1">legal@uniledge.io</a>
                  </p>
                  <p className="flex items-center">
                    <Mail size={16} className="mr-2 text-gray-500" />
                    General Support: <a href="mailto:support@uniledge.io" className="text-blue-600 hover:underline ml-1">support@uniledge.io</a>
                  </p>
                  <p className="flex items-center">
                    <ExternalLink size={16} className="mr-2 text-gray-500" />
                    Website: <a href="https://uniledge.io" className="text-blue-600 hover:underline ml-1">https://uniledge.io</a>
                  </p>
                </div>
              </div>
            </section>

          </div>

          {/* Termly Link Banner (Bottom) */}
          <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 mt-8">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span className="text-gray-600 text-sm">
                This policy is automatically updated to comply with the latest regulations.
              </span>
              <a 
                href={termlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:underline text-sm font-medium"
              >
                View on Termly
                <ExternalLink size={14} className="ml-1" />
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
            <p>© {new Date().getFullYear()} UniLedge LLC. All rights reserved.</p>
            <div className="mt-2">
              <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link>
              <span className="mx-2">|</span>
              <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
              <span className="mx-2">|</span>
              <Link to="/cookies" className="text-blue-600 hover:underline">Cookie Policy</Link>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;